// ============================================
// IEEE 754 half-float (f16) CPU-side packing
// ============================================
//
// Used by the sprite batcher to pack HDR vertex colors as float16x4. The fast path goes through the
// native Float16Array (Chromium ≥135 / recent Bun); the bit-twiddle fallback keeps unit tests and
// older runtimes working. Both paths round-to-nearest and clamp overflow to ±Inf (f16 max 65504).

type Float16ArrayLike = { buffer: ArrayBufferLike; [index: number]: number };
type Float16ArrayCtor = new (length: number) => Float16ArrayLike;

const NativeFloat16Array = (globalThis as { Float16Array?: Float16ArrayCtor }).Float16Array;

/** Convert a JS number to its f16 bit pattern (uint16). */
export let toHalf: (value: number) => number;

if (NativeFloat16Array) {
  const f16 = new NativeFloat16Array(1);
  const u16 = new Uint16Array(f16.buffer);
  toHalf = (value: number): number => {
    f16[0] = value;
    return u16[0];
  };
} else {
  const f32 = new Float32Array(1);
  const u32 = new Uint32Array(f32.buffer);
  toHalf = (value: number): number => {
    f32[0] = value;
    const x = u32[0];
    const sign = (x >>> 16) & 0x8000;
    const exp = (x >>> 23) & 0xff;
    let mant = x & 0x007fffff;

    if (exp === 0xff) {
      // Inf / NaN
      return sign | 0x7c00 | (mant ? 0x0200 : 0);
    }

    const e = exp - 127 + 15;
    if (e >= 0x1f) return sign | 0x7c00; // overflow → ±Inf
    if (e <= 0) {
      if (e < -10) return sign; // underflow → ±0
      // Subnormal: shift mantissa (with implicit leading 1) into place, round to nearest
      mant |= 0x00800000;
      const shift = 14 - e;
      let half = mant >> shift;
      if ((mant >> (shift - 1)) & 1) half += 1;
      return sign | half;
    }

    // Normal: drop 13 mantissa bits, round to nearest (carry may legally bump the exponent)
    let half = sign | (e << 10) | (mant >> 13);
    if (mant & 0x1000) half += 1;
    return half;
  };
}

/** f16 bit pattern → JS number (test/debug helper; not used on hot paths). */
export function fromHalf(bits: number): number {
  const sign = bits & 0x8000 ? -1 : 1;
  const exp = (bits >>> 10) & 0x1f;
  const mant = bits & 0x03ff;
  if (exp === 0) return sign * mant * 2 ** -24; // subnormal / zero
  if (exp === 0x1f) return mant ? NaN : sign * Infinity;
  return sign * (1 + mant / 1024) * 2 ** (exp - 15);
}
